
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { toErrorMessage } from '@/utils/errorHandling';
import { formatJobTimestamp } from '@/utils/bulkJob';
import EnterpriseMenuPullStartForm from './EnterpriseMenuPullStartForm.vue';
import BulkJobProgress from '@/components/shared/bulk-job/BulkJobProgress.vue';
import BulkJobHistory from '@/components/shared/bulk-job/BulkJobHistory.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import Toast from '@/utils/toast';

const namespace: string = 'enterpriseMenuPull';

@Component<EnterpriseMenuPull>({
	components: {
		EnterpriseMenuPullStartForm,
		BulkJobProgress,
		BulkJobHistory,
		SuitesHeader,
		TextButton
	}
})
export default class EnterpriseMenuPull extends Vue {
	@Action('fetchActiveJob', { namespace }) private fetchActiveJob!: () => Promise<void>;
	@Action('fetchViewedJob', { namespace }) private fetchViewedJob!: (jobId: string) => Promise<void>;
	@Action('fetchJobHistory', { namespace }) private fetchJobHistory!: (params?: TableFilterParams) => Promise<void>;
	@Action('cancelJob', { namespace }) private cancelJob!: (jobId: string) => Promise<void>;
	@Action('stopPolling', { namespace }) private stopPolling!: () => void;
	@Action('clearViewedJob', { namespace }) private clearViewedJobAction!: () => void;
	@Action('setTenant', { namespace: 'auth' }) private setTenant!: (tenantId: string) => Promise<void>;
	@Getter('getActiveJob', { namespace }) private activeJob!: BulkJob | null;
	@Getter('getViewedJob', { namespace }) private viewedJob!: BulkJob | null;
	@Getter('hasActiveJob', { namespace }) private hasActiveJob!: boolean;
	@Getter('getJobHistory', { namespace }) private jobHistory!: BulkJob[];
	@Getter('getJobHistoryCount', { namespace }) private jobHistoryCount!: number;
	@Getter('getJobHistoryFilters', { namespace }) private historyFilters!: TableFilterParams;

	private prefillLocationIds: string[] = [];
	private isInitializing: boolean = true;
	private isLoadingHistory: boolean = false;
	private isCancelling: boolean = false;

	private get viewedJobTitle(): string {
		if (!this.viewedJob) {
			return '';
		}
		return this.$t('settings.enterprise_menu_pull.history.viewing_title', { time: formatJobTimestamp(this.viewedJob.startedAt) });
	}

	private get totalHistoryPages(): number {
		const limit: number = this.historyFilters.limit || 10;
		return Math.max(1, Math.ceil(this.jobHistoryCount / limit));
	}

	private async created(): Promise<void> {
		try {
			await this.fetchActiveJob();
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isInitializing = false;
		}
	}

	private beforeDestroy(): void {
		this.stopPolling();
		this.clearViewedJobAction();
	}

	private async viewHistoryJob(jobId: string): Promise<void> {
		try {
			await this.fetchViewedJob(jobId);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	private clearViewedJob(): void {
		this.clearViewedJobAction();
	}

	private async onCancel(jobId: string): Promise<void> {
		this.isCancelling = true;
		try {
			await this.cancelJob(jobId);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isCancelling = false;
		}
	}

	/**
	 * Open a location's menus. Stop polling before switching tenants so no stale poll
	 * requests fire against the new tenant's axios context.
	 *
	 * @param {string} restaurantId
	 * @return {Promise<void>}
	 */
	private async onOpenLocation(restaurantId: string): Promise<void> {
		this.stopPolling();
		try {
			await this.setTenant(restaurantId);
			this.$router.push('/menus').catch(() => {});
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
	}

	private onRetryFailed(restaurantIds: string[]): void {
		this.prefillLocationIds = restaurantIds;
		this.clearViewedJobAction();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	private async applyHistoryFilters(next: TableFilterParams): Promise<void> {
		this.isLoadingHistory = true;
		try {
			await this.fetchJobHistory(next);
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isLoadingHistory = false;
		}
	}

	private async goToPreviousHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current > 1) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current - 1 });
		}
	}

	private async goToNextHistoryPage(): Promise<void> {
		const current: number = this.historyFilters.page || 1;
		if (current < this.totalHistoryPages) {
			await this.applyHistoryFilters({ ...this.historyFilters, page: current + 1 });
		}
	}

	private async onHistorySortChange(columnName: string): Promise<void> {
		const nextDirection: 'asc' | 'desc' = this.historyFilters.sortBy === columnName
			? (this.historyFilters.sortDirection === 'asc' ? 'desc' : 'asc')
			: 'asc';
		await this.applyHistoryFilters({
			...this.historyFilters,
			page: 1,
			sortBy: columnName,
			sortDirection: nextDirection
		});
	}
}
